import { ContractInsightsData } from '@/pages/ContractDetail';

import { StatCard } from '@/components/ui/stat-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format, differenceInDays } from 'date-fns';
import { Wrench, Shield, FileCheck, RefreshCw, Sparkles, Settings, HardHat } from 'lucide-react';

interface Props {
    data: ContractInsightsData;
}

const getMaintenanceIcon = (type?: string) => {
    const normalized = type?.toLowerCase() || '';
    if (normalized.includes('insurance')) return <Shield className="h-4 w-4" />;
    if (normalized.includes('warranty')) return <FileCheck className="h-4 w-4" />;
    if (normalized.includes('conversion')) return <RefreshCw className="h-4 w-4" />;
    if (normalized.includes('refinement')) return <Sparkles className="h-4 w-4" />;
    if (normalized.includes('servicing')) return <Settings className="h-4 w-4" />;
    if (normalized.includes('refurbishment')) return <HardHat className="h-4 w-4" />;
    return <Wrench className="h-4 w-4" />;
};

/** Strips GH₵/GHS amounts, comma-separated numbers, and bare amounts from text */
function stripAmounts(text: string): string {
    return text
        .replace(/GH[₵S]\s*[\d,]+(\.\d+)?/gi, '')    // GH₵1,300 or GHS500
        .replace(/\b\d{1,3}(,\d{3})+(\.\d+)?\b/g, '') // 1,000 or 3,030
        .replace(/\b\d{2,}\b/g, '')                    // bare amounts: 300, 950, 70, etc.
        .replace(/\s{2,}/g, ' ')                       // collapse double spaces
        .replace(/\s+([,.])/g, '$1')                   // fix orphan punctuation
        .trim();
}

export default function ContractInsightMaintenance({ data }: Props) {
    const maintenanceEvents = (data.maintenances || []).sort((a, b) =>
        new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
    );

    const maintenanceCount = maintenanceEvents.length;
    const lastMaintenance = maintenanceEvents[0];

    // Most common maintenance type
    const typeCounts: Record<string, number> = {};
    maintenanceEvents.forEach((m) => {
        const type = m.type?.toLowerCase() || 'other';
        typeCounts[type] = (typeCounts[type] || 0) + 1;
    });
    const mostCommonType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    // Average interval between maintenance events (days)
    let avgInterval = 'N/A';
    if (maintenanceEvents.length >= 2) {
        let totalDays = 0;
        for (let i = 0; i < maintenanceEvents.length - 1; i++) {
            totalDays += Math.abs(differenceInDays(
                new Date(maintenanceEvents[i].started_at),
                new Date(maintenanceEvents[i + 1].started_at),
            ));
        }
        const avg = Math.round(totalDays / (maintenanceEvents.length - 1));
        avgInterval = `${avg} days`;
    }

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
                <StatCard
                    title="Most Common Type"
                    value={<span className="capitalize">{mostCommonType}</span>}
                    description="The most frequently occurring type of maintenance service for this vehicle."
                />
                <StatCard
                    title="Maintenance Count"
                    value={`${maintenanceCount} Records`}
                    description="Total number of individual maintenance service events logged for this vehicle."
                />
                <StatCard
                    title="Last Maintenance"
                    value={lastMaintenance ? format(new Date(lastMaintenance.started_at), 'MMM d, yyyy') : 'N/A'}
                    description="Date of the most recent maintenance service performed on this vehicle."
                />
                <StatCard
                    title="Avg. Interval"
                    value={avgInterval}
                    description="Average number of days between consecutive maintenance events."
                />
            </div>

            {/* Maintenance History Section */}
            <div>
                <div className="flex flex-row items-center justify-between pb-4">
                    <h3 className="text-lg font-semibold tracking-tight">
                        Maintenance History
                    </h3>
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono">
                            {maintenanceCount} Records
                        </Badge>
                    </div>
                </div>

                {maintenanceEvents.length > 0 ? (
                    <>
                        {/* Mobile: Card layout */}
                        <div className="lg:hidden space-y-3">
                            {maintenanceEvents.map((event) => (
                                <div key={event.id} className="group p-4 rounded-xl border border-border/40 bg-card flex flex-col gap-2 shadow-sm">
                                    <div className="flex items-start justify-between">
                                        <div className="flex gap-3">
                                            <div className="bg-muted/30 rounded-full h-10 w-10 flex items-center justify-center font-medium">
                                                {getMaintenanceIcon(event.type || undefined)}
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-sm">
                                                        {format(new Date(event.started_at), 'MMM d, yyyy')}
                                                    </span>
                                                    {event.type && (
                                                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 capitalize bg-background rounded-full">
                                                            {event.type.toLowerCase()}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <span className="text-xs text-muted-foreground mt-0.5 max-w-[250px] truncate">
                                                    {stripAmounts(event.description || 'Maintenance service')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop: Table layout */}
                        <div className="hidden lg:block">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Description</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {maintenanceEvents.map((event) => (
                                        <TableRow key={event.id}>
                                            <TableCell className="whitespace-nowrap font-medium">
                                                {format(new Date(event.started_at), 'MMM d, yyyy')}
                                            </TableCell>
                                            <TableCell>
                                                {event.type ? (
                                                    <Badge variant="outline" className="capitalize">
                                                        {event.type.toLowerCase()}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="max-w-[400px] truncate text-muted-foreground">
                                                {stripAmounts(event.description || 'Maintenance service')}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-xl bg-muted/20">
                        <Wrench className="h-8 w-8 mx-auto mb-3 opacity-30" />
                        <p className="text-sm font-medium">No maintenance records</p>
                    </div>
                )}
            </div>
        </div>
    );
}
